import React, { Fragment, ChangeEvent } from "react";
import { RouteComponentProps } from "react-router";

import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import FileUploadDialog from "../FileUploadDialog";
import HasRight from "../HasRight";
import PhotoRoll from "../PhotoRoll";

import { V2 } from "../../Api";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

interface IHealthFairPhotosViewState {
    isPhotosLoading: boolean | string;
    isReceiptsLoading: boolean | string;

    isDeleteOpen: boolean;
    deleteId: number;

    isUploadOpen: boolean;
    uploadDescription: string;
    uploadEndpoint: string;
    uploadType: "photos" | "receipts";
    uploadFiles?: FileList
    uploadExtra: { [key: string]: string };
    preventUpload: boolean;
    receiptTotal: string;
    receiptVendor: string;
    receiptNote: string;

    photos: any[];
    receipts: any[];

    [name: string]: any;
}

class HealthFairPhotosView extends React.Component<RouteComponentProps<any> & IAuthProps, IHealthFairPhotosViewState> {
    constructor(props: Readonly<RouteComponentProps<any> & IAuthProps>) {
        super(props);

        const state: IHealthFairPhotosViewState = {
            isPhotosLoading: true,
            isReceiptsLoading: true,

            isDeleteOpen: false,
            deleteId: -1,

            isUploadOpen: false,
            uploadDescription: "",
            uploadEndpoint: "",
            uploadType: "photos",
            uploadExtra: {},
            preventUpload: true,
            receiptTotal: "",
            receiptVendor: "",
            receiptNote: "",

            photos: [],
            receipts: []
        };

        this.state = state;

        this.openUpload = this.openUpload.bind(this);
        this.closeUpload = this.closeUpload.bind(this);
        this.showPhotoUpload = this.showPhotoUpload.bind(this);
        this.showReceiptUpload = this.showReceiptUpload.bind(this);
        this.handleUploadSuccess = this.handleUploadSuccess.bind(this);
        this.handlePhotoDelete = this.handlePhotoDelete.bind(this);
        this.handleReceiptDelete = this.handleReceiptDelete.bind(this);
        this.handlePhotoUploadRequest = this.handlePhotoUploadRequest.bind(this);
        this.handleReceiptUploadRequest = this.handleReceiptUploadRequest.bind(this);
        this.handleDeletionConfirmation = this.handleDeletionConfirmation.bind(this);
        this.closeDeletion = this.closeDeletion.bind(this);
        this.handleReceiptTotalChanged = this.handleReceiptTotalChanged.bind(this);
    }

    public async componentDidMount() {
        await this.loadPhotos();
        await this.loadReceipts();
    }

    public componentWillUnmount() {
        // Clean up blob URLs
        this.state.photos.forEach(a => URL.revokeObjectURL(a.url || a));
        this.state.receipts.forEach(a => URL.revokeObjectURL(a.url || a));
    }

    public render() {
        return (
            <div className="viewer-root">
                <FileUploadDialog isOpen={this.state.isUploadOpen} closeOnSuccess={true} allowMultiple={true} preventUpload={this.state.preventUpload} extraData={this.state.uploadExtra} accept="image/jpeg, image/png" endpoint={this.state.uploadEndpoint} descriptionOverride={this.state.uploadDescription} filesOverride={this.state.uploadFiles} onRequestClose={this.closeUpload} onUploadSuccess={this.handleUploadSuccess}>
                    {this.state.uploadType === "receipts"
                        ? <Fragment>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="receiptTotal">Total Amount</label>
                                    <input type="number" name="receiptTotal" value={this.state.receiptTotal} min={0} step={0.01} onChange={this.handleReceiptTotalChanged} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="receiptVendor">Vendor</label>
                                    <input type="text" name="receiptVendor" value={this.state.receiptVendor} onChange={this.handleReceiptTotalChanged} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="receiptNote">Note <small>(optional)</small></label>
                                    <textarea name="receiptNote" value={this.state.receiptNote} onChange={this.handleReceiptTotalChanged} />
                                </div>
                            </section>
                        </Fragment>
                        : null
                    }
                </FileUploadDialog>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteOpen} description="Are you sure you want to delete this image?" onConfirm={this.handleDeletionConfirmation} onRequestClose={this.closeDeletion}>
                    <div className="photo-deletion-box">
                        {this.state.uploadType === "photos" && this.state.isDeleteOpen && this.state.photos[this.state.deleteId] && <img src={this.state.photos[this.state.deleteId].url} alt="Thumbnail" />}
                        {this.state.uploadType === "receipts" && this.state.isDeleteOpen && this.state.receipts[this.state.deleteId] && <img src={this.state.receipts[this.state.deleteId].url} alt="Thumbnail" />}
                    </div>
                </DeletionConfirmationDialog>
                <HasRight right="fair:p:s:read">
                    <h3>Social Media</h3>
                    {typeof this.state.isPhotosLoading === "string"
                        ? <div className="error-box">{this.state.isPhotosLoading}</div>
                        : <Fragment>
                            <PhotoRoll photos={this.state.photos.map(a => a.url || a)} canDelete={this.props.authContext.hasRight("fair:p:s:delete")} onRequestDelete={this.handlePhotoDelete} onRequestUpload={this.handlePhotoUploadRequest} />
                            {this.state.isPhotosLoading === true ? <div className="spinner-outer-text"><span className="spinner lds-dual-ring-small" />&nbsp;Loading photos...</div> : null}
                        </Fragment>
                    }
                    &nbsp;
                    <div className="action-panel-dialog">
                        <HasRight right="fair:p:s:upload">
                            <button type="button" className="button text-with-icon" onClick={this.showPhotoUpload}><i className="icon-upload" /><span>Upload Photo</span></button>
                        </HasRight>
                    </div>
                </HasRight>
                <HasRight right="fair:p:r:read">
                    <h3>Receipts</h3>
                    {typeof this.state.isReceiptsLoading === "string"
                        ? <div className="error-box">{this.state.isReceiptsLoading}</div>
                        : <Fragment>
                            <PhotoRoll photos={this.state.receipts} canDelete={this.props.authContext.hasRight("fair:p:r:delete")} onRequestDelete={this.handleReceiptDelete} onRequestUpload={this.handleReceiptUploadRequest} />
                            {this.state.isReceiptsLoading === true ? <div className="spinner-outer-text"><span className="spinner lds-dual-ring-small" />&nbsp;Loading receipts...</div> : null}
                        </Fragment>
                    }
                    &nbsp;
                    <div className="action-panel-dialog">
                        <HasRight right="fair:p:r:upload">
                            <button type="button" className="button text-with-icon" onClick={this.showReceiptUpload}><i className="icon-upload" /><span>Upload Receipt</span></button>
                        </HasRight>
                    </div>
                </HasRight>
            </div>
        );
    }

    private closeUpload() {
        this.setState({
            isUploadOpen: false,
            uploadFiles: undefined,

            receiptNote: "",
            receiptTotal: "",
            receiptVendor: ""
        });
    }

    private openUpload() {
        this.setState({
            isUploadOpen: true
        });
    }

    private showPhotoUpload() {
        this.setState({
            uploadDescription: "Health Fair Photo",
            uploadEndpoint: `fairs/${this.props.match.params.id}/photos`,
            uploadType: "photos",
            preventUpload: false,
        }, () => this.openUpload());
    }

    private showReceiptUpload() {
        this.setState({
            uploadDescription: "Health Fair Receipt",
            uploadEndpoint: `fairs/${this.props.match.params.id}/receipts`,
            uploadType: "receipts",
            preventUpload: true,
        }, () => this.openUpload());
    }

    private async loadPhotos() {
        try {
            this.state.photos.forEach(a => URL.revokeObjectURL(a.url));

            if (!this.props.authContext.hasRight("fair:p:s:read")) {
                return;
            }

            const photos = await V2.get<any[]>(`fairs/${this.props.match.params.id}/photos`);
            if (photos.length === 0) {
                this.setState({
                    isPhotosLoading: false,
                    photos: [],
                });
            }
            const imageLinks = photos.map(() => true);

            // Load images in one-by-one
            this.setState({
                photos: imageLinks
            }, () => {
                photos.forEach((photo, index) => {
                    V2.get<Blob>(`files/${photo}`).then(a => URL.createObjectURL(a)).then(a => {
                        this.setState(s => {
                            s.photos[index] = { url: a, id: photo };

                            return { photos: s.photos, isPhotosLoading: s.photos.some(b => b === true) };
                        });
                    });
                });
            });
        }
        catch (e) {
            this.setState({
                isPhotosLoading: e.message || e || "Could not load photos"
            });
        }
    }

    private async loadReceipts() {
        try {
            this.state.receipts.forEach(a => URL.revokeObjectURL(a.url || a));

            if (!this.props.authContext.hasRight("fair:p:r:read")) {
                return;
            }

            const photos = await V2.get<any[]>(`fairs/${this.props.match.params.id}/receipts`);
            if (photos.length === 0) {
                this.setState({
                    isReceiptsLoading: false,
                    receipts: [],
                });
            }

            const imageLinks = photos.map(a => true);

            // Load images in one-by-one
            this.setState({
                receipts: imageLinks
            }, () => {
                photos.forEach((photo, index) => {
                    V2.get<Blob>(`files/${photo.id}`).then(a => URL.createObjectURL(a)).then(a => {
                        this.setState(s => {
                            s.receipts[index] = { url: a, id: photo.id, description: `${photo.vendor} - $${photo.total}` };

                            return { receipts: s.receipts, isReceiptsLoading: s.receipts.some(b => b === true) };
                        });
                    });
                });
            });
        }
        catch (e) {
            this.setState({
                isReceiptsLoading: e.message || e || "Could not load photos"
            });
        }
    }

    private async handleUploadSuccess() {
        switch (this.state.uploadType) {
            case "photos":
                await this.loadPhotos();
                break;
            case "receipts":
                await this.loadReceipts();
                break;
            default:
                break;
        }
    }

    private async handleDeletionConfirmation() {
        switch (this.state.uploadType) {
            case "photos":
                await this.realPhotoDelete(this.state.deleteId);
                break;
            case "receipts":
                await this.realReceiptDelete(this.state.deleteId);
                break;
            default:
                break;
        }
    }

    private handlePhotoDelete(index: number) {
        this.setState({
            isDeleteOpen: true,
            deleteId: index,
            uploadType: "photos"
        });
    }

    private handleReceiptDelete(index: number) {
        this.setState({
            isDeleteOpen: true,
            deleteId: index,
            uploadType: "receipts"
        });
    }

    private closeDeletion() {
        this.setState({
            isDeleteOpen: false,
        });
    }

    private async realPhotoDelete(index: number) {
        try {
            this.setState({
                isPhotosLoading: true
            });

            await V2.delete(`fairs/${this.props.match.params.id}/photos/${this.state.photos[index].id}`);

            this.setState(s => {
                s.photos.splice(index, 1);

                return { photos: s.photos, isPhotosLoading: false }
            });
        }
        catch (e) {
            this.setState({
                isPhotosLoading: e.message || e || "Could not delete photo"
            });
        }
    }

    private async realReceiptDelete(index: number) {
        try {
            this.setState({
                isReceiptsLoading: true
            });

            await V2.delete(`fairs/${this.props.match.params.id}/receipts/${this.state.receipts[index].id}`);

            this.setState(s => {
                s.receipts.splice(index, 1);

                return { receipts: s.receipts, isReceiptsLoading: false }
            });
        }
        catch (e) {
            this.setState({
                isReceiptsLoading: e.message || e || "Could not delete receipt"
            });
        }
    }

    private handlePhotoUploadRequest(files: FileList) {
        this.setState({
            uploadFiles: files
        }, () => this.showPhotoUpload());
    }

    private handleReceiptUploadRequest(files: FileList) {
        this.setState({
            uploadFiles: files
        }, () => this.showReceiptUpload());
    }

    private handleReceiptTotalChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => {
            const preventUpload = isNaN(parseFloat(this.state.receiptTotal)) || !this.state.receiptVendor.trim();

            this.setState({
                preventUpload,
                uploadExtra: {
                    note: this.state.receiptNote,
                    total: this.state.receiptTotal,
                    vendor: this.state.receiptVendor,
                }
            });
        });
    }
}

export default withTitle("Health Fair Photos", withAuth(HealthFairPhotosView));